import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTheme } from './ThemeContext';
import type { ServerVoice } from '../lib/welcome-voice';
import {
  isWelcomeVoiceOn,
  playServerGreeting,
  serverVoiceStatus,
  setWelcomeVoiceOn,
  stopGreeting,
} from '../lib/welcome-voice';

/**
 * Appearance: light or dark, an accent, and the colour of the primary action.
 *
 * Applied by writing CSS custom properties onto the document root, so a change
 * lands on every surface in the same frame — nothing re-renders and nothing
 * re-fetches. Every component already resolves its colour through these
 * variables, which is what makes a live swap possible at all.
 *
 * The accent rewrites the brand ramp itself, so links, focus rings, hairlines,
 * the active nav item and the tile chips all follow it together. Picking them
 * off one at a time is how a product ends up with four different blues.
 */

export interface Swatch {
  key: string;
  name: string;
  /** light, mid, deep — the three stops every component samples from. */
  stops: [string, string, string];
}

/** The accent set. Gold is the brand; the rest are deliberate alternatives. */
export const ACCENTS: Swatch[] = [
  { key: 'gold', name: 'SkillXzen gold', stops: ['#F7D07A', '#F0B030', '#D99416'] },
  { key: 'azure', name: 'Azure', stops: ['#7FB4FF', '#3884F6', '#1F63CC'] },
  { key: 'teal', name: 'Teal', stops: ['#6EE7DA', '#2DD4BF', '#12A594'] },
  { key: 'indigo', name: 'Indigo', stops: ['#A5AEFB', '#818CF8', '#5A63D8'] },
  { key: 'violet', name: 'Violet', stops: ['#C4A6FB', '#9A6DF5', '#7342CE'] },
  { key: 'rose', name: 'Rose', stops: ['#F7A8BE', '#EC5F86', '#C63A62' ] },
];

/** The primary action. Kept separate: plenty of brands accent one way and
 *  act another, and a button is the one control you never want ambiguous. */
export const ACTIONS: Swatch[] = [
  { key: 'gold', name: 'Gold', stops: ['#F7C65A', '#F0B030', '#D99416'] },
  { key: 'navy', name: 'Navy', stops: ['#3E4E86', '#28305F', '#1B2246'] },
  { key: 'azure', name: 'Azure', stops: ['#5A9BFF', '#2C6FE0', '#1B51AC'] },
  { key: 'emerald', name: 'Emerald', stops: ['#5FD9A0', '#16A96B', '#0E7F50'] },
  { key: 'graphite', name: 'Graphite', stops: ['#5A6274', '#39404F', '#252A35'] },
];

const ACCENT_KEY = 'sx.accent';
const ACTION_KEY = 'sx.action';

function read(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/** Does this colour need dark text on top of it to stay readable? */
function needsDarkText(hex: string): boolean {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  // Contrast against black vs white; whichever wins gets the label.
  return (luminance + 0.05) / 0.05 > 1.05 / (luminance + 0.05);
}

export function applyAppearance(accentKey: string, actionKey: string): void {
  const root = document.documentElement;

  const accent = ACCENTS.find((s) => s.key === accentKey) ?? ACCENTS[0]!;
  const action = ACTIONS.find((s) => s.key === actionKey) ?? ACTIONS[0]!;

  // The brand ramp. Everything that reads as "accent" resolves through these.
  root.style.setProperty('--sx-gold-300', accent.stops[0]);
  root.style.setProperty('--sx-gold-400', accent.stops[1]);
  root.style.setProperty('--sx-gold-500', accent.stops[2]);
  root.style.setProperty('--sx-primary', accent.stops[1]);
  root.style.setProperty('--sx-border-focus', accent.stops[1]);

  // The primary action, plus the label colour that keeps it readable — a dark
  // action with dark text is the usual way a themable button goes wrong.
  root.style.setProperty('--sx-action-from', action.stops[0]);
  root.style.setProperty('--sx-action-mid', action.stops[1]);
  root.style.setProperty('--sx-action-to', action.stops[2]);
  root.style.setProperty('--sx-action-text', needsDarkText(action.stops[1]) ? '#1b1b1b' : '#ffffff');
  root.style.setProperty(
    '--sx-action-glow',
    `${action.stops[1]}cc`,
  );
}

interface AppearanceValue {
  accent: string;
  setAccent: (key: string) => void;
  action: string;
  setAction: (key: string) => void;
}

const AppearanceContext = createContext<AppearanceValue | null>(null);

/**
 * One source of truth for the whole app.
 *
 * Two pickers now read this — the top bar menu and the panel on Profile — and
 * with a hook holding its own state each would have kept a private copy: change
 * the accent on Profile and the dot in the top bar would still show the old one
 * until it happened to remount.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [accent, setAccent] = useState(() => read(ACCENT_KEY, 'gold'));
  const [action, setAction] = useState(() => read(ACTION_KEY, 'gold'));

  useEffect(() => {
    applyAppearance(accent, action);
    try {
      window.localStorage.setItem(ACCENT_KEY, accent);
      window.localStorage.setItem(ACTION_KEY, action);
    } catch {
      /* the choice simply will not survive a reload */
    }
  }, [accent, action]);

  const value = useMemo(
    () => ({ accent, setAccent, action, setAction }),
    [accent, action],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceValue {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error('useAppearance must be used inside AppearanceProvider');
  return context;
}

/**
 * The picker itself, shared by both places it appears.
 *
 * `layout` only changes how much room it takes: the top bar gets the compact
 * form, Profile gets names beside the swatches because there is space for them
 * and a settings page should say what it is offering.
 */
export function AppearancePicker({ layout = 'compact' }: { layout?: 'compact' | 'full' }) {
  const { theme, toggle } = useTheme();
  const { accent, setAccent, action, setAction } = useAppearance();
  const full = layout === 'full';
  // Read once on open rather than held in context: nothing else in the
  // application reacts to it, and the greeting reads the stored value for
  // itself at the moment it would speak.
  const [voice, setVoice] = useState(isWelcomeVoiceOn());

  // Which voice this browser will actually use, resolved the same way the
  // greeting resolves it. Shown because "is it using the Indian voice?" is
  // otherwise unanswerable without a developer console — and because the
  // answer changes when someone installs a voice, which is exactly when
  // somebody wants to check.
  const [server, setServer] = useState<ServerVoice | null>(null);
  useEffect(() => {
    let cancelled = false;
    void serverVoiceStatus().then((status) => {
      if (!cancelled) setServer(status);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const heardAs = server ? server.voice : 'No recording installed';

  return (
    <div className={`picker${full ? ' picker--full' : ''}`}>
      <p className="appearance__group">Mode</p>
      <div className="appearance__modes">
        <button
          type="button"
          className={`appearance__mode${theme === 'light' ? ' is-on' : ''}`}
          onClick={() => theme === 'dark' && toggle()}
          aria-pressed={theme === 'light'}
        >
          ◐ Light
        </button>
        <button
          type="button"
          className={`appearance__mode${theme === 'dark' ? ' is-on' : ''}`}
          onClick={() => theme === 'light' && toggle()}
          aria-pressed={theme === 'dark'}
        >
          ☾ Dark
        </button>
      </div>

      <p className="appearance__group">Accent</p>
      <div className="appearance__swatches">
        {ACCENTS.map((swatch) => (
          <button
            key={swatch.key}
            type="button"
            className={`swatch${accent === swatch.key ? ' is-on' : ''}`}
            style={
              {
                background: `linear-gradient(135deg, ${swatch.stops[0]}, ${swatch.stops[2]})`,
                '--swatch-colour': `linear-gradient(135deg, ${swatch.stops[0]}, ${swatch.stops[2]})`,
              } as CSSProperties
            }
            onClick={() => setAccent(swatch.key)}
            aria-pressed={accent === swatch.key}
            aria-label={swatch.name}
            title={swatch.name}
          >
            {full ? <span className="swatch__name">{swatch.name}</span> : null}
          </button>
        ))}
      </div>

      <p className="appearance__group">Welcome</p>
      <div className="appearance__modes">
        <button
          type="button"
          className={`appearance__mode${voice ? ' is-on' : ''}`}
          onClick={() => {
            setWelcomeVoiceOn(true);
            setVoice(true);
          }}
          aria-pressed={voice}
        >
          🔊 Spoken
        </button>
        <button
          type="button"
          className={`appearance__mode${voice ? '' : ' is-on'}`}
          onClick={() => {
            setWelcomeVoiceOn(false);
            setVoice(false);
            // Stop mid-sentence if it is playing right now, rather than
            // making someone sit through the greeting they just switched off.
            stopGreeting();
          }}
          aria-pressed={!voice}
        >
          🔇 Silent
        </button>
      </div>

      <p className="appearance__voice">
        <span className="appearance__voicename" title={heardAs}>
          {heardAs}
        </span>
        {server ? (
          <button
            type="button"
            className="appearance__voicetest"
            onClick={() => void playServerGreeting()}
          >
            Test
          </button>
        ) : null}
      </p>

      <p className="appearance__group">Buttons</p>
      <div className="appearance__swatches">
        {ACTIONS.map((swatch) => (
          <button
            key={swatch.key}
            type="button"
            className={`swatch${action === swatch.key ? ' is-on' : ''}`}
            style={
              {
                background: `linear-gradient(135deg, ${swatch.stops[0]}, ${swatch.stops[2]})`,
                '--swatch-colour': `linear-gradient(135deg, ${swatch.stops[0]}, ${swatch.stops[2]})`,
              } as CSSProperties
            }
            onClick={() => setAction(swatch.key)}
            aria-pressed={action === swatch.key}
            aria-label={swatch.name}
            title={swatch.name}
          >
            {full ? <span className="swatch__name">{swatch.name}</span> : null}
          </button>
        ))}
      </div>

      {full ? (
        <div className="picker__preview">
          <p className="appearance__group">Preview</p>
          <div className="picker__row">
            <button type="button" className="btn">
              Primary action
            </button>
            <button type="button" className="btn btn--ghost">
              Secondary
            </button>
            <a href="#preview" onClick={(event) => event.preventDefault()}>
              A link
            </a>
          </div>
        </div>
      ) : null}

      {/* Only in the full layout: in the popover it was three lines of prose
          above a grid of six circles, which is not a trade worth making. */}
      {full ? (
        <p className="appearance__note">
          Applies instantly and is remembered on this device. The logo keeps its own colours.
        </p>
      ) : null}
    </div>
  );
}

/** The control in the top bar. */
export function AppearanceMenu() {
  const { accent } = useAppearance();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!wrap.current?.contains(event.target as Node)) close();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const current = ACCENTS.find((swatch) => swatch.key === accent) ?? ACCENTS[0]!;

  return (
    <div className="appearance" ref={wrap}>
      <button
        type="button"
        className="appearance__btn"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Appearance"
        title="Appearance"
      >
        <span
          className="appearance__dot"
          aria-hidden="true"
          style={{ background: `linear-gradient(135deg, ${current.stops[0]}, ${current.stops[2]})` }}
        />
      </button>

      {open ? (
        <div className="appearance__panel" role="dialog" aria-label="Appearance">
          <AppearancePicker />
        </div>
      ) : null}
    </div>
  );
}
